import { z } from 'zod'
import { COMMS_KEY } from './comms'

// Communications, Phase 4 (ADR 0037; docs/architecture/BRE-Communications-Plan.md
// §10 Phase 4): journeys and event alerts.
//
// A journey is a Hub workflow in ADR 0020's sense: an editable draft, frozen
// into an immutable version when someone asks for its approval; an approved
// version goes live by hand; every enrollment keeps the version it joined,
// whatever goes live later. A trigger enrolls people (each person once per
// journey, or once per event for an event reminder); the steps wait, send a
// template on one channel, or check that the person still qualifies. Every
// message a journey sends goes through the same outbox, gates, quiet hours
// and caps as a campaign's, so caps count journeys and campaigns together.

// ----------------------------------------------------------------- triggers

export const JOURNEY_TRIGGER_KINDS = ['marketing_grant', 'event_upcoming', 'member_lapsed'] as const
export type JourneyTriggerKind = (typeof JOURNEY_TRIGGER_KINDS)[number]
export const JOURNEY_TRIGGER_LABELS: Record<JourneyTriggerKind, string> = {
  marketing_grant: 'Someone subscribes to marketing',
  event_upcoming: 'A night someone holds tickets to is coming up',
  member_lapsed: 'A myBRE member hasn’t been active for a while',
}

export const JourneyTriggerSchema = z.discriminatedUnion('kind', [
  // A marketing grant for the journey's brand that took effect after the
  // version went live (a confirmed double opt-in counts when it confirms),
  // on this channel or any.
  z.object({ kind: z.literal('marketing_grant'), channel: z.enum(['email', 'sms', 'push', 'any']) }),
  // This long before a BTownTix performance the person holds a live ticket
  // for (from BTownTix's holders read).
  z.object({ kind: z.literal('event_upcoming'), leadHours: z.number().int().min(1).max(720) }),
  // A myBRE member whose last Rewards activity is older than this (and who
  // has had some activity: someone who never used Rewards hasn't lapsed).
  z.object({ kind: z.literal('member_lapsed'), inactiveDays: z.number().int().min(7).max(730) }),
])
export type JourneyTrigger = z.infer<typeof JourneyTriggerSchema>

// ------------------------------------------------------------------- steps

export const JOURNEY_CHECKS = ['still_consented', 'still_holds_tickets', 'still_lapsed'] as const
export type JourneyCheck = (typeof JOURNEY_CHECKS)[number]
export const JOURNEY_CHECK_LABELS: Record<JourneyCheck, string> = {
  still_consented: 'Still subscribed to this brand’s marketing on a channel the journey uses',
  still_holds_tickets: 'Still holds a ticket for the night',
  still_lapsed: 'Still hasn’t been active in myBRE',
}

const STEP_KEY = z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/)
export const JourneyStepSchema = z.discriminatedUnion('type', [
  // Wait this long (minutes; the editor offers days and hours).
  z.object({ key: STEP_KEY, type: z.literal('wait'), minutes: z.number().int().min(1).max(60 * 24 * 120) }),
  // Send one template on its channel. templateVersionId is filled in when a
  // version is frozen (the template's current version then); a draft names
  // the template only.
  z.object({ key: STEP_KEY, type: z.literal('send'), channel: z.enum(['email', 'sms', 'push']), templateId: z.string().min(1).max(60), templateVersionId: z.string().min(1).max(60).nullable().default(null) }),
  // Go on only if the check passes; otherwise the person leaves the journey.
  z.object({ key: STEP_KEY, type: z.literal('check'), check: z.enum(JOURNEY_CHECKS) }),
])
export type JourneyStep = z.infer<typeof JourneyStepSchema>
export const JOURNEY_STEP_LABELS: Record<JourneyStep['type'], string> = { wait: 'Wait', send: 'Send', check: 'Check' }

export const JourneyDefinitionSchema = z.object({
  trigger: JourneyTriggerSchema,
  steps: z.array(JourneyStepSchema).min(1).max(20),
})
export type JourneyDefinition = z.infer<typeof JourneyDefinitionSchema>

// What stops a definition from being asked for approval, in plain words.
// (Template channel, brand and purpose, and the event-message rule, are
// checked by Platform Core against the data.)
export function journeyIssues(d: JourneyDefinition): string[] {
  const issues: string[] = []
  const keys = new Set<string>()
  for (const s of d.steps) {
    if (keys.has(s.key)) issues.push(`Two steps are both called “${s.key}”`)
    keys.add(s.key)
  }
  if (!d.steps.some((s) => s.type === 'send')) issues.push('A journey needs at least one send step')
  if (d.steps.at(-1)?.type === 'wait') issues.push('A journey can’t end with a wait')
  for (const s of d.steps) {
    if (s.type !== 'check') continue
    if (s.check === 'still_holds_tickets' && d.trigger.kind !== 'event_upcoming') issues.push('“Still holds a ticket” only makes sense for an event reminder')
    if (s.check === 'still_lapsed' && d.trigger.kind !== 'member_lapsed') issues.push('“Still hasn’t been active” only makes sense for a lapsed-member journey')
  }
  return [...new Set(issues)]
}

export const durationLabel = (minutes: number) => {
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes === 1440 ? '' : 's'}`
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes === 60 ? '' : 's'}`
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

// ----------------------------------------------------------- the lifecycle

export const JOURNEY_STATUSES = ['draft', 'active', 'paused', 'retired'] as const
export type JourneyStatus = (typeof JOURNEY_STATUSES)[number]
export const JOURNEY_STATUS_LABELS: Record<JourneyStatus, string> = { draft: 'Draft', active: 'Live', paused: 'Paused', retired: 'Retired' }

export const JOURNEY_VERSION_STATUSES = ['awaiting_approval', 'approved', 'rejected', 'live', 'superseded'] as const
export type JourneyVersionStatus = (typeof JOURNEY_VERSION_STATUSES)[number]
export const JOURNEY_VERSION_STATUS_LABELS: Record<JourneyVersionStatus, string> = {
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved, not live yet',
  rejected: 'Not approved',
  live: 'Live',
  superseded: 'Replaced (enrolled people finish it)',
}

export const ENROLLMENT_STATUSES = ['active', 'completed', 'exited'] as const
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number]
export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = { active: 'In the journey', completed: 'Finished', exited: 'Left early' }

// Why someone left a journey early.
export const JOURNEY_EXIT_REASONS = {
  consent_withdrawn: 'Unsubscribed from this brand’s marketing on the channels it uses',
  suppressed: 'Suppressed on every channel this journey uses',
  person_inactive: 'Person no longer active or merged away',
  no_longer_holds_tickets: 'No longer holds a ticket for the night',
  event_changed: 'The night was canceled or postponed',
  event_passed: 'The night has passed',
  active_again: 'Active in myBRE again',
  journey_retired: 'The journey was retired',
  check_failed: 'A check didn’t pass',
} as const
export type JourneyExitReason = keyof typeof JOURNEY_EXIT_REASONS

export const JOURNEY_EVENT_KINDS = ['enrolled', 'waiting', 'queued', 'not_sent', 'check_passed', 'exited', 'completed'] as const
export type JourneyEventKind = (typeof JOURNEY_EVENT_KINDS)[number]

// --------------------------------------------------------------- request bodies

export const JourneyBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).nullable(),
  brandKey: z.string().regex(COMMS_KEY),
  purposeKey: z.string().regex(COMMS_KEY),
  definition: JourneyDefinitionSchema,
  expectedVersion: z.number().int().min(1).optional(),
})
export type JourneyBody = z.infer<typeof JourneyBodySchema>

// ---------------------------------------------------------------- views

export type JourneySummary = {
  id: string
  name: string
  description: string | null
  triggerKind: JourneyTriggerKind
  brandKey: string
  purposeKey: string
  status: JourneyStatus
  liveVersion: number | null
  latestVersion: number | null
  latestVersionStatus: JourneyVersionStatus | null
  provisionalNote: string | null
  counts: { active: number; completed: number; exited: number }
  updatedAt: string
}

// ------------------------------------------------------------ event alerts

export const EVENT_ALERT_STATUSES = ['to_review', 'sending', 'sent', 'dismissed'] as const
export type EventAlertStatus = (typeof EVENT_ALERT_STATUSES)[number]
export const EVENT_ALERT_STATUS_LABELS: Record<EventAlertStatus, string> = { to_review: 'To review', sending: 'Sending', sent: 'Sent', dismissed: 'Dismissed' }

export const EventAlertEditSchema = z.object({
  pushTitle: z.string().trim().min(1).max(60),
  pushBody: z.string().trim().min(1).max(180),
  pushUrl: z
    .string()
    .trim()
    .max(500)
    .regex(/^https:\/\/\S+$/)
    .nullable(),
  smsText: z.string().trim().min(1).max(300).nullable(),
  expectedVersion: z.number().int().min(1),
})

export const EventAlertConfirmSchema = z.object({
  channels: z.array(z.enum(['push', 'sms'])).min(1).max(2),
  // The audience the person saw: confirming is refused if it changed.
  expectedAudienceHash: z.string().length(64),
  expectedVersion: z.number().int().min(1),
})

// The words a new alert starts with, per kind of change (staff edit them
// before confirming). Plain and short: BTownTix's own email carries the
// details, the refund window and the new tickets.
export function eventAlertDraft(change: { kind: string; eventTitle: string; startsAtLabel: string | null; previousStartsAtLabel: string | null; venueName: string | null }): { title: string; body: string } {
  const t = change.eventTitle.length > 40 ? `${change.eventTitle.slice(0, 39)}…` : change.eventTitle
  const was = change.previousStartsAtLabel ? `The ${change.previousStartsAtLabel} show` : 'Your show'
  switch (change.kind) {
    case 'postponed':
      return { title: `${t} is postponed`, body: `${was} is postponed and your tickets stay valid. BTownTix has emailed you the details and your options.` }
    case 'rescheduled':
      return { title: `${t} has a new date`, body: `The show moved to ${change.startsAtLabel ?? 'a new date'}. Your tickets carry over; BTownTix has emailed you the new ones.` }
    case 'canceled':
      // BTownTix sends no cancellation email (D-485): its refund emails go
      // out per order when staff run the refunds.
      return { title: `${t} is canceled`, body: `We’re sorry: ${change.previousStartsAtLabel ? `the ${change.previousStartsAtLabel} show` : 'your show'} is canceled. BTownTix will email you when your refund is made.` }
    case 'time_changed':
      return { title: `${t}: new start time`, body: `The show now starts ${change.startsAtLabel ?? 'at a new time'}. Your tickets stay the same.` }
    default:
      return { title: `${t}: new venue`, body: `The show is now at ${change.venueName ?? 'a new venue'}. Your tickets stay valid; check BTownTix for directions.` }
  }
}

// ----------------------------------------------------- the content calendar

// One item on the master calendar's Communications layer (ADR 0018, 0037).
export type CommsCalendarKind = 'campaign' | 'journey_send' | 'event_alert'
